import { cloudfrontImagesBaseURL } from '../../../components/JobStepper/staticValues';
import { IEditableJobSteps } from './buildEditJobBody';

const text = (value: unknown): string => (value == null ? '' : String(value));
const number = (value: unknown): string => (value == null || value === '' ? '' : String(Number(value)));
const logoPath = (value: string | null): string => text(value).replace(cloudfrontImagesBaseURL, '');
const isUrlDelivery = (value: string | boolean): boolean => value === true || value === '1';
const description = (value: string | null): string => {
  const trimmed = text(value).trim();
  if (!trimmed || trimmed === '<p><br></p>') return '';
  // Quill wraps plain text from older listings in a paragraph when it loads it.
  return trimmed.startsWith('<') ? trimmed : `<p>${trimmed}</p>`;
};

// Compare fields that are saved, not react-hook-form's generated array IDs or UI-only flags.
// Null and an empty input are equivalent; the forms use both representations.
const comparable = (steps: IEditableJobSteps) => {
  const { step1, step2, step3, step4, step5 } = steps;
  const urlDelivery = isUrlDelivery(step5.applicationUrlEnabled);
  return {
    step1: {
      jobTitle: text(step1.jobTitle),
      companyName: text(step1.companyName),
      industrySectorId: number(step1.industrySectorId),
      employmentTypeId: number(step1.employmentTypeId),
      supplierId: number(step1.supplierId),
      supplierTitle: text(step1.supplierTitle),
    },
    step2: {
      salaryUnitId: number(step2.salaryUnitId),
      salaryMin: number(step2.salaryMin),
      salaryMax: number(step2.salaryMax),
      salaryExact: number(step2.salaryExact),
      salaryUnitRadio: number(step2.salaryUnitRadio),
      locations: (step2.locations || []).map((location) => ({
        country: location.country === 'USA' ? 'US' : text(location.country),
        address1: text(location.address1),
        address2: text(location.address2),
        city: text(location.city),
        state: text(location.state),
        province: text(location.province),
        zip: text(location.zip),
      })),
    },
    step3: {
      jobDescription: description(step3.jobDescription),
      shortDescription: text(step3.shortDescription).trim(),
    },
    step4: { uploadedLogoPath: logoPath(step4.uploadedLogoPath) },
    step5: {
      applicationUrlEnabled: urlDelivery,
      applicationUrl: urlDelivery ? text(step5.applicationUrl) : '',
      applicationEmails: urlDelivery ? [] : (step5.tempAdditionalEmails || [])
        .map((item) => text(item.email).trim())
        .filter(Boolean),
      contactFirstName: text(step5.contactFirstName),
      contactLastName: text(step5.contactLastName),
      email: text(step5.email),
    },
  };
};

export const hasEditJobChanges = (current: IEditableJobSteps, saved: IEditableJobSteps): boolean => (
  JSON.stringify(comparable(current)) !== JSON.stringify(comparable(saved))
);

/** Whether two job descriptions are the same as far as the unsaved-changes check is concerned. */
export const isSameJobDescription = (a: string | null, b: string | null): boolean => (
  description(a) === description(b)
);
